import { useEffect, useState } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BarChart3, Check, Clock, Lock, Loader2, Trophy, Users, Eye } from "lucide-react";

const API = `${getApiUrl()}/api`;

function closesLabel(endsAt, isClosed) {
  if (!endsAt) return "";
  const end = new Date(endsAt);
  if (isClosed) return `Closed ${end.toLocaleDateString()}`;
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return "Closing…";
  const mins = Math.floor(ms / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days >= 1) return `Closes in ${days}d ${hours}h`;
  if (hours >= 1) return `Closes in ${hours}h ${mins % 60}m`;
  return `Closes in ${mins}m`;
}

export function NewsPoll({ poll, updateId, onPollUpdate }) {
  const [selected, setSelected] = useState(poll.userVote || []);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelected(poll.userVote || []);
  }, [poll.userVote]);

  const isClosed = !!poll.isClosed;
  const maxSel = poll.maxSelections || 1;
  const showResults = !!poll.canSeeResults;
  const totalVotes = poll.totalVotes || 0;
  const hasVoted = (poll.userVote || []).length > 0;

  // Winner(s) = option(s) with the highest count, once results are revealed.
  const revealed = showResults && !!poll.counts;
  const maxCount = revealed
    ? Math.max(0, ...(poll.options || []).map((o) => poll.counts[o.id] || 0))
    : 0;
  const winners = revealed && maxCount > 0
    ? (poll.options || []).filter((o) => (poll.counts[o.id] || 0) === maxCount)
    : [];
  const isWinner = (id) => winners.some((w) => w.id === id);
  const winnerPct = revealed && totalVotes > 0 ? Math.round((maxCount / totalVotes) * 100) : 0;

  const toggle = (id) => {
    if (isClosed || submitting) return;
    setSelected((prev) => {
      if (maxSel === 1) return [id];
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxSel) return prev; // cap reached
      return [...prev, id];
    });
  };

  const submitVote = async (e) => {
    e.stopPropagation();
    if (selected.length === 0) {
      toast.error("Pick an option first");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/community/updates/${updateId}/poll/vote`, { optionIds: selected });
      onPollUpdate?.(data.poll);
      toast.success(hasVoted ? "Vote updated" : "Vote counted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit vote");
    } finally {
      setSubmitting(false);
    }
  };

  const selectionChanged =
    selected.length !== (poll.userVote || []).length ||
    selected.some((id) => !(poll.userVote || []).includes(id));

  return (
    <div
      className="mt-3 rounded-lg border border-purple-500/25 bg-purple-500/5 p-3"
      onClick={(e) => e.stopPropagation()}
      data-testid={`news-poll-${updateId}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 className="w-4 h-4 text-purple-400 shrink-0" />
          <p className="text-white font-medium text-sm truncate">{poll.question}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border bg-white/5 text-white/60 border-white/10 font-semibold tabular-nums"
            title={`${totalVotes} ${totalVotes === 1 ? "vote" : "votes"} cast`}
            data-testid={`news-poll-votecount-${updateId}`}
          >
            <Users className="w-3 h-3" />
            {totalVotes}
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-semibold ${
              isClosed
                ? "bg-white/5 text-white/50 border-white/10"
                : "bg-purple-500/15 text-purple-300 border-purple-400/30"
            }`}
            data-testid={`news-poll-status-${updateId}`}
          >
            {isClosed ? <Lock className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {closesLabel(poll.endsAt, isClosed)}
          </span>
        </div>
      </div>

      {isClosed && showResults && (
        <div
          className="flex items-center gap-2 mb-2 rounded-md border border-amber-400/30 bg-amber-400/10 px-2.5 py-1.5"
          data-testid={`news-poll-summary-${updateId}`}
        >
          <Trophy className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-amber-200/90 text-xs">
            {totalVotes === 0 ? (
              <span>Poll closed — no votes were cast.</span>
            ) : winners.length === 1 ? (
              <span>
                Winner: <span className="font-semibold text-amber-100">{winners[0].text}</span>
                {" "}— {maxCount} {maxCount === 1 ? "vote" : "votes"} ({winnerPct}%)
              </span>
            ) : (
              <span>
                Tie ({winnerPct}%): <span className="font-semibold text-amber-100">{winners.map((w) => w.text).join(", ")}</span>
              </span>
            )}
          </p>
        </div>
      )}

      {poll.adminPreview && (
        <div
          className="flex items-center gap-1.5 mb-2 text-[11px] text-cyan-300/90 bg-cyan-500/10 border border-cyan-400/25 rounded-md px-2.5 py-1"
          data-testid={`news-poll-adminpreview-${updateId}`}
        >
          <Eye className="w-3.5 h-3.5 shrink-0" />
          Live results (admin only) — voters can&apos;t see these yet.
        </div>
      )}

      <div className="space-y-1.5">
        {(poll.options || []).map((opt) => {
          const isSel = selected.includes(opt.id);
          const count = showResults && poll.counts ? poll.counts[opt.id] || 0 : 0;
          const pct = showResults && totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const isMyVote = (poll.userVote || []).includes(opt.id);
          const won = isClosed && isWinner(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              disabled={isClosed || submitting}
              className={`w-full text-left rounded-md border transition-colors ${
                won
                  ? "border-amber-400/60 bg-amber-400/5"
                  : isSel
                  ? "border-purple-400/60 bg-purple-500/10"
                  : "border-white/10 bg-black/20 hover:border-white/25"
              } ${isClosed ? "cursor-default" : "cursor-pointer"}`}
              data-testid={`news-poll-opt-${updateId}-${opt.id}`}
            >
              <div className="px-2.5 py-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 min-w-0">
                    {won ? (
                      <Trophy className="w-4 h-4 shrink-0 text-amber-400" />
                    ) : (
                      <span
                        className={`w-4 h-4 shrink-0 flex items-center justify-center border ${
                          maxSel === 1 ? "rounded-full" : "rounded"
                        } ${isSel ? "bg-purple-500 border-purple-500" : "border-white/30"}`}
                      >
                        {isSel && <Check className="w-3 h-3 text-white" />}
                      </span>
                    )}
                    <span className={`text-sm truncate ${won ? "text-amber-100 font-medium" : "text-white/90"}`}>{opt.text}</span>
                    {isMyVote && showResults && (
                      <span className="text-[9px] text-purple-300/80 shrink-0">your pick</span>
                    )}
                  </span>
                  {showResults && (
                    <span className={`text-xs shrink-0 tabular-nums font-medium ${won ? "text-amber-200/90" : "text-white/70"}`}>{pct}% · {count}</span>
                  )}
                </div>
                {showResults && (
                  <div
                    className="mt-1.5 h-2 w-full rounded-full bg-white/10 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    data-testid={`news-poll-bar-${updateId}-${opt.id}`}
                  >
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${won ? "bg-amber-400" : "bg-purple-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2 mt-2.5">
        <p className="text-white/40 text-xs">
          {maxSel > 1 ? `Pick up to ${maxSel}` : "Single choice"}
          {!showResults && !isClosed ? " · results after you vote" : ""}
          {isClosed ? " · voting closed" : ""}
        </p>
        {!isClosed && (
          <Button
            size="sm"
            onClick={submitVote}
            disabled={submitting || selected.length === 0 || (hasVoted && !selectionChanged)}
            className="h-7 text-xs bg-purple-600 hover:bg-purple-500 text-white"
            data-testid={`news-poll-vote-${updateId}`}
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : hasVoted ? "Update vote" : "Vote"}
          </Button>
        )}
      </div>
    </div>
  );
}
